<script setup lang="ts">
// #719 観点B: モバイルナビ背景に敷くキャンプのマップ画像。
import campMapBg from '@/assets/images/map/AreaMap_AdventurersCamp.webp'

const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const { isMobile } = useIsMobile()

useSeoMeta({
  title: '冒険者のキャンプ | Code & Magic',
  description: '冒険者があつまるキャンプです。',
  ogTitle: '冒険者のキャンプ | Code & Magic',
  ogDescription: '冒険者があつまるキャンプです。',
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  // #719 観点B: レイアウトは template の <NuxtLayout :name> で SSR から確定させる。
  middleware: ['authenticated', 'confirm-game-state'],
  layout: false,
  usePageReady: true,
})

const { getTicker } = useSocialApi()
const { finishLoading } = usePageReady()
const { reloadGameState } = useGameStateStore()

const tickerItems = ref<import('~/composables/useSocialApi').TickerItem[]>([])

// 魔導士の名のり 編集 (#417). `?edit=profile` で来たら自動で開く（閲覧
// ページの編集ボタンが冒険者のキャンプ経由でこのダイアログを開く想定）。
// ダイアログ内部で slug 設定済/未設定の両方を扱うので、呼び出し側は
// v-model を切り替えるだけで OK。
const profileEditDialog = ref(false)

const openProfileEdit = () => {
  profileEditDialog.value = true
}

const onProfileSaved = async () => {
  await reloadGameState()
}

// ?edit=profile (旧: ?edit=seal) で来たら編集ダイアログを開く。閲覧ページの
// 「編集」ボタンからキャンプ経由で起動する導線をサポートする。
const shouldOpenProfile = () =>
  route.query.edit === 'profile' || route.query.edit === 'seal'

onMounted(async () => {
  // 編集ダイアログの起動は finishLoading に依存させない。userInfo は
  // confirm-game-state middleware で確実にロード済みなので、即時開ける。
  if (shouldOpenProfile()) {
    void openProfileEdit()
  }

  await finishLoading()

  // Fetch ticker in background (non-blocking)
  getTicker()
    .then((res: { items: import('~/composables/useSocialApi').TickerItem[] }) => { tickerItems.value = res.items })
    .catch(() => { /* silent fail — ticker is decorative */ })
})

// 同一ページに残ったまま ?edit=profile が後から付いたケースもカバーする
// (SPA 内遷移で onMounted が再発火しない可能性に対する保険)。
watch(() => route.query.edit, (v) => {
  if (v === 'profile' || v === 'seal') {
    void openProfileEdit()
  }
})
</script>

<template>
  <NuxtLayout :name="isMobile ? 'mobile-nav' : 'centering43'">
    <SceneMap
      scene-id="wld_adventurersCamp"
      :hide-back-button="true"
      :mobile="isMobile"
      :mobile-background="campMapBg"
      :mobile-scene-ids="['wld_encodia', 'cmp_arcaneLab', 'cmp_cradle', 'cmp_emporium']"
    >
      <template #map>
        <v-img
          src="@/assets/images/map/AreaMap_AdventurersCamp.webp"
          width="100%"
          height="auto"
          lazy-src="@/assets/images/map/LazyAreaMap_AdventurersCamp.png"
          :transition="false"
        />
        <ActivityTicker :items="tickerItems" />

        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1536 1152"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          xml:space="preserve"
          xmlns:serif="http://www.serif.com/"
          style="
          fill-rule: evenodd;
          clip-rule: evenodd;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-miterlimit: 1.5;
        "
        >
          <path
            data-scene-id="cmp_arcaneLab"
            d="M505.951,434.883c19.081,-33.876 49.721,-69.921 128.712,-77.682c88.869,-8.731 256.995,20.005 316.348,51.901c43.3,23.271 57.819,34.353 71.236,63.096c22.276,47.717 46.813,93.851 33.244,145.187c-9.412,35.609 -80.848,116.58 -134.332,152.65c-53.484,36.071 -132.601,45.407 -189.286,44.099c-69.881,-1.612 -117.032,-17.526 -161.47,-49.526c-38.527,-27.743 -80.798,-75.367 -88.876,-116.693c-10.743,-54.954 -21.438,-131.608 24.424,-213.032Z"
            class="scene"
          />
          <path
            data-scene-id="cmp_cradle"
            d="M135.519,201.498c36.523,-2.6 101.993,1.131 131.619,16.961c22.645,12.101 36.824,40.979 44.777,66.488c9.272,29.739 11.475,79.133 4.749,116.014c-6.897,37.823 -24.424,84.467 -46.134,110.926c-20.462,24.938 -51.94,49.958 -84.127,47.83c-33.357,-2.205 -90.346,-29.964 -116.014,-61.06c-25.668,-31.095 -35.078,-81.897 -37.993,-125.512c-2.94,-43.986 3.392,-96.339 20.353,-126.869c15.041,-27.074 51.877,-42.578 82.77,-44.778Z"
            class="scene"
          />
          <path
            data-scene-id="cmp_emporium"
            d="M1349.26,200.741c58.459,17.187 114.745,37.579 132.975,73.351c25.422,49.883 12.502,76.331 17.64,114.657c5.653,42.177 -24.085,153.653 -61.739,187.251c-37.653,33.598 -124.947,22.479 -164.184,14.338c-31.078,-6.449 -54.315,-36.332 -71.236,-63.186c-19.675,-31.224 -47.049,-102.84 -42.742,-146.859c5.653,-57.781 15.378,-90.989 27.816,-118.413c9.049,-19.952 26.946,-36.898 46.813,-46.134c25.781,-11.986 79.188,-25.433 114.657,-15.005Z"
            class="scene"
          />
          <path
            data-scene-id="wld_encodia"
            d="M755.608,295.802c36.575,-0.395 78.38,-2.45 113.311,-18.996c19.583,-9.277 46.316,-81.478 51.561,-95.661c4.498,-12.161 9.192,-77.908 8.142,-101.088c-0.81,-17.865 -8.82,-80.057 -8.82,-80.057l-327.011,-0c0,-0 -8.141,49.241 -8.141,75.307c0,32.834 -5.029,57.67 7.463,102.019c11.359,40.324 38.673,80.977 65.809,97.444c24.612,14.936 41.875,21.636 97.686,21.032Z"
            class="scene"
          />
        </svg>

        <!-- 魔導士の名のり 編集 (#417) — 公開閲覧ページ /c/<slug> の編集ボタン
           から ?edit=profile で誘導され、ここで開く。SceneMap は default slot
           を描画しないため `#map` slot の内側に置く必要がある。
           (v-dialog は teleport で body に描画されるので、配置場所自体は
           表示には影響しない。) -->
        <DialogMagicianProfileEditDialog
          v-model="profileEditDialog"
          @saved="onProfileSaved"
        />
      </template>
    </SceneMap>
  </NuxtLayout>
</template>
